import { Component, ElementRef, EventEmitter, HostListener, inject, Input, Output, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';

type PaginaVisivel = number | '...';

@Component({
  selector: 'app-paginador',
  standalone: true,
  imports: [MatIcon],
  templateUrl: './paginador.component.html',
  styleUrl: './paginador.component.css'
})
export class PaginadorComponent {
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 0;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  @Output() pageChange = new EventEmitter<PageEvent>();

  private readonly elementRef = inject(ElementRef);

  dropdownAberto = signal(false);

  @HostListener('document:click', ['$event'])
  aoClicarFora(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownAberto.set(false);
    }
  }

  alternarDropdown() {
    this.dropdownAberto.update(aberto => !aberto);
  }

  selecionarTamanhoPagina(novoTamanho: number) {
    this.dropdownAberto.set(false);
    this.alterarTamanhoPagina(novoTamanho);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.length / this.pageSize));
  }

  get inicioRegistro(): number {
    return this.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  get fimRegistro(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.length);
  }

  get paginasVisiveis(): PaginaVisivel[] {
    const total = this.totalPaginas;
    const atual = this.pageIndex + 1;

    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const paginas: PaginaVisivel[] = [1];
    if (atual > 3) {
      paginas.push('...');
    }

    const inicio = Math.max(2, atual - 1);
    const fim = Math.min(total - 1, atual + 1);
    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    if (atual < total - 2) {
      paginas.push('...');
    }
    paginas.push(total);

    return paginas;
  }

  alterarTamanhoPagina(novoTamanho: number) {
    this.pageSize = novoTamanho;
    this.pageIndex = 0;
    this.emitir();
  }

  irParaPagina(pagina: PaginaVisivel) {
    if (pagina === '...' || pagina === this.pageIndex + 1) {
      return;
    }
    this.pageIndex = pagina - 1;
    this.emitir();
  }

  navegar(acao: 'first' | 'prev' | 'next' | 'last') {
    switch (acao) {
      case 'first':
        this.pageIndex = 0;
        break;
      case 'prev':
        this.pageIndex = Math.max(0, this.pageIndex - 1);
        break;
      case 'next':
        this.pageIndex = Math.min(this.totalPaginas - 1, this.pageIndex + 1);
        break;
      case 'last':
        this.pageIndex = this.totalPaginas - 1;
        break;
    }
    this.emitir();
  }

  get naPrimeiraPagina(): boolean {
    return this.pageIndex === 0;
  }

  get naUltimaPagina(): boolean {
    return this.pageIndex >= this.totalPaginas - 1;
  }

  private emitir() {
    this.pageChange.emit({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }
}
